import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { LogoMark, LogoLockup } from '../components/Logo';

/**
 * MIRROR — landing page
 *
 * Migrated to the Mirror doctrine (see MIRROR.md):
 *   - No icons (lucide-react import is gone).
 *   - No animated node graph, no glow, no glassmorphism.
 *   - No emoji.
 *   - Monospaced primary type. Editorial serif reserved for confronting prose.
 *   - One accent (muted blood red) used only on the gap-marker line.
 *   - Hairline borders, square corners, no shadows.
 *
 * Existing translation keys are preserved so copy stays under content control.
 * The visual structure is rebuilt as a clinical document:
 *   I.   Statement (hero)
 *   II.  Demo box (typed input, no animation)
 *   III. The four pillars (Stream / Reality Report / Reservoir / Premortem)
 *   IV.  The gap example (Stated vs. Lived)
 *   V.   The questions (FAQ)
 *   VI.  The threshold (final CTA)
 *
 * Translation keys that don't exist yet fall back to English literals so the
 * page never renders a missing-string token. Copy is intentionally restrained.
 */

function SectionRule() {
  return <hr className="rule my-16" />;
}

function SectionMarker({ numeral, label }) {
  return (
    <div className="flex items-baseline gap-4 mb-8">
      <span className="mirror-numeral">{numeral}</span>
      <span className="section-label">{label}</span>
    </div>
  );
}

export default function LandingPage() {
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [demoText, setDemoText] = useState('');
  const [demoResult, setDemoResult] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [showDemoSignup, setShowDemoSignup] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const { login, register, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [memoryCount, setMemoryCount] = useState(1783);
  useEffect(() => {
    axios.get(`${API_URL}/api/stats/public`).then(res => {
      setMemoryCount(1783 + (res.data?.total_memories || 0));
    }).catch(() => {});
  }, []);

  const handleDemoExtract = async () => {
    if (!demoText.trim() || demoText.length > 500) return;
    setDemoLoading(true); setDemoError(''); setDemoResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/demo/extract`, {
        text: demoText,
        language: language || 'en',
      });
      setDemoResult(res.data); setShowDemoSignup(true);
    } catch (err) {
      setDemoError(err.response?.status === 429 ? t('landing.demoLimitReached') : t('landing.demoError'));
    } finally { setDemoLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setIsLoading(true);
    try {
      if (isLogin) {
        const u = await login(email, password);
        navigate(u.profile_completed ? '/dashboard' : '/onboarding', { replace: true });
      } else {
        await register(email, password, name);
        navigate('/onboarding', { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setIsLoading(false);
    }
  };

  const FAQ_ITEMS = t('landing.faq') || [];
  const carousel = t('landing.carousel') || [];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO title="Verum Q — Infrastructure for honesty" path="/" />

      {/* ── Nav ── */}
      <nav className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center shrink-0" aria-label="Verum Q">
            <span className="sm:hidden"><LogoMark size={22} /></span>
            <span className="hidden sm:inline-flex"><LogoLockup size={22} /></span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector variant="ghost" />
            {user ? (
              <Button
                className="button-mirror"
                onClick={() => navigate(user.profile_completed ? '/dashboard' : '/onboarding')}
                data-testid="nav-dashboard-btn"
              >
                {t('dashboard.dashboard') || 'Dashboard'}
              </Button>
            ) : (
              <>
                <button
                  type="button"
                  className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => { setShowAuth(true); setIsLogin(true); }}
                  data-testid="nav-login-btn"
                >
                  {t('auth.signIn')}
                </button>
                <Button
                  className="button-mirror"
                  onClick={() => { setShowAuth(true); setIsLogin(false); }}
                  data-testid="nav-signup-btn"
                >
                  {t('auth.getStarted')}
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── I. Statement (hero) ── */}
      <section className="px-6 pt-24 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 animate-fade-in">
              <span className="mirror-numeral">I.</span>
              <h1
                className="mt-6 text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-foreground"
                data-testid="hero-headline"
              >
                {t('landing.heroTitle')}
              </h1>
              <p className="mt-8 text-base text-muted-foreground leading-relaxed max-w-xl font-editorial">
                {t('landing.heroSubtitle')}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs uppercase tracking-wider text-muted-foreground" data-testid="hero-trust-row">
                <span data-testid="hero-privacy">{t('landing.heroPrivacy')}</span>
                <span className="text-border">/</span>
                <span data-testid="memory-counter">
                  <span className="text-foreground">{memoryCount.toLocaleString()}</span>{' '}
                  {t('landing.memoriesPreserved')}
                </span>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  className="button-mirror"
                  onClick={() => { setShowAuth(true); setIsLogin(false); }}
                  data-testid="hero-get-started-btn"
                >
                  {t('landing.startYourMap')}
                </Button>
                <Button
                  className="button-mirror button-mirror--ghost"
                  onClick={() => {
                    const demo = document.querySelector('[data-testid="demo-textarea"]');
                    if (demo) demo.focus();
                  }}
                  data-testid="hero-example-btn"
                >
                  {t('landing.secondaryCta')}
                </Button>
              </div>

              <p className="mt-10 text-xs text-muted-foreground max-w-md font-editorial italic" data-testid="hero-stat">
                {t('landing.heroStat')}
              </p>
            </div>

            {/* Demo box — typographic, no chrome. */}
            <div className="lg:col-span-5 animate-fade-in animation-delay-200">
              <div className="mirror-frame">
                <div className="mirror-meta mb-4">{t('landing.demoTryIt') || 'Demo'}</div>
                <textarea
                  className="w-full min-h-[140px] bg-transparent border-0 border-b border-border focus:border-foreground focus:outline-none p-0 text-sm resize-none placeholder:text-muted-foreground/50 font-mono"
                  placeholder={t('landing.demoPlaceholder')}
                  value={demoText}
                  onChange={(e) => setDemoText(e.target.value.slice(0, 500))}
                  data-testid="demo-textarea"
                />
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xs text-muted-foreground font-mono">{demoText.length}/500</span>
                  <Button
                    size="sm"
                    className="button-mirror"
                    onClick={handleDemoExtract}
                    disabled={!demoText.trim() || demoLoading}
                    data-testid="demo-extract-btn"
                  >
                    {demoLoading ? t('common.loading') : t('landing.demoExtract')}
                  </Button>
                </div>

                {demoError && (
                  <p className="mt-4 text-xs text-accent font-mono uppercase tracking-wider">
                    {demoError}
                  </p>
                )}

                {demoResult && (
                  <div className="mt-6 pt-6 border-t border-border space-y-3 animate-fade-in" data-testid="demo-result">
                    {demoResult.people?.length > 0 && (
                      <div className="grid grid-cols-[80px_1fr] gap-3 text-xs">
                        <span className="mirror-meta">People</span>
                        <span className="text-foreground">{demoResult.people.join(', ')}</span>
                      </div>
                    )}
                    {demoResult.places?.length > 0 && (
                      <div className="grid grid-cols-[80px_1fr] gap-3 text-xs">
                        <span className="mirror-meta">Places</span>
                        <span className="text-foreground">{demoResult.places.join(', ')}</span>
                      </div>
                    )}
                    {demoResult.dates?.length > 0 && (
                      <div className="grid grid-cols-[80px_1fr] gap-3 text-xs">
                        <span className="mirror-meta">Dates</span>
                        <span className="text-foreground">{demoResult.dates.join(', ')}</span>
                      </div>
                    )}
                    {demoResult.questions?.length > 0 && (
                      <div className="pt-4 mt-4 border-t border-border space-y-3" data-testid="demo-questions">
                        <p className="mirror-meta">{t('landing.demoQuestionsLabel')}</p>
                        {demoResult.questions.map((q, i) => (
                          <p
                            key={`demo-q-${i}`}
                            className="text-sm text-foreground font-editorial leading-snug"
                            data-testid={`demo-question-${i}`}
                          >
                            {q}
                          </p>
                        ))}
                      </div>
                    )}
                    {showDemoSignup && (
                      <p className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
                        {t('landing.demoSignupPrompt')}{' '}
                        <button
                          className="text-foreground underline underline-offset-4 hover:no-underline transition-all"
                          onClick={() => { setShowAuth(true); setIsLogin(false); }}
                          data-testid="demo-signup-link"
                        >
                          {t('landing.demoSignupCta')}
                        </button>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionRule />

      {/* ── II. The four pillars ── */}
      <section className="px-6">
        <div className="max-w-6xl mx-auto">
          <SectionMarker numeral="II." label={t('landing.howItWorks') || 'The four pillars'} />

          <h2 className="text-2xl sm:text-3xl tracking-tight text-foreground max-w-3xl">
            {t('landing.yourLifeBeautifullyMapped')}
          </h2>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {[
              { titleKey: 'landing.features.captureMemories.title', descKey: 'landing.features.captureMemories.description', label: 'Stream' },
              { titleKey: 'landing.features.aiPowered.title', descKey: 'landing.features.aiPowered.description', label: 'Reality Report' },
              { titleKey: 'landing.features.buildTimeline.title', descKey: 'landing.features.buildTimeline.description', label: 'Reservoir' },
              { titleKey: 'landing.features.rediscoverConnections.title', descKey: 'landing.features.rediscoverConnections.description', label: 'Premortem' },
            ].map((f, i) => (
              <div key={f.titleKey} className="bg-background p-6 animate-fade-in" style={{ animationDelay: `${i * 60}ms` }}>
                <p className="mirror-meta">{f.label}</p>
                <h3 className="mt-3 text-base font-medium text-foreground">{t(f.titleKey)}</h3>
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{t(f.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionRule />

      {/* ── III. Conversation showcase ── */}
      {carousel.length > 0 && (
        <section className="px-6" data-testid="conversation-section">
          <div className="max-w-6xl mx-auto">
            <SectionMarker numeral="III." label={t('landing.conversationLabel') || 'In practice'} />
            <h2 className="text-2xl sm:text-3xl tracking-tight text-foreground max-w-3xl">
              {t('landing.conversationTitle')}
            </h2>
            <p className="mt-4 text-sm text-muted-foreground max-w-2xl font-editorial">
              {t('landing.conversationSubtitle')}
            </p>

            <div className="mt-12 grid md:grid-cols-2 gap-6">
              {carousel.slice(0, 4).map((ex, i) => (
                <div
                  key={`exchange-${i}`}
                  className="mirror-frame animate-fade-in"
                  style={{ animationDelay: `${i * 80}ms` }}
                  data-testid={`conversation-exchange-${i}`}
                >
                  <p className="mirror-meta">{t('landing.conversationYou')}</p>
                  <p className="mt-2 text-sm text-foreground font-editorial leading-relaxed">
                    {ex.you}
                  </p>
                  <hr className="rule my-5" />
                  <p className="mirror-meta">{t('landing.conversationModusNode')}</p>
                  <p className="mt-2 text-sm text-foreground leading-relaxed">
                    {ex.ai}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <SectionRule />

      {/* ── IV. The gap (stated vs lived) — the *only* place red appears at scale. ── */}
      <section className="px-6" data-testid="compass-section">
        <div className="max-w-5xl mx-auto">
          <SectionMarker numeral="IV." label={t('landing.compassLabel') || 'The gap'} />
          <h2 className="text-2xl sm:text-3xl tracking-tight text-foreground max-w-3xl">
            {t('landing.compassTitle')}
          </h2>
          <p className="mt-4 text-sm text-muted-foreground max-w-2xl font-editorial">
            {t('landing.compassSubtitle')}
          </p>

          <div className="mt-12 grid sm:grid-cols-2 gap-px bg-border border border-border">
            <div className="bg-background p-8">
              <p className="mirror-meta">{t('landing.compassStated') || 'Stated'}</p>
              <ul className="mt-4 space-y-2">
                {(t('landing.compassStatedValues') || []).map((v, i) => (
                  <li key={`stated-${i}`} className="text-sm text-foreground">{v}</li>
                ))}
              </ul>
            </div>
            <div className="bg-background p-8">
              <p className="mirror-meta mirror-meta--accent">{t('landing.compassLived') || 'Lived'}</p>
              <ul className="mt-4 space-y-2">
                {(t('landing.compassLivedValues') || []).map((v, i) => (
                  <li key={`lived-${i}`} className="text-sm text-foreground">{v}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* The single confronting line. Editorial serif. Underline-by-rule. */}
          <div className="mt-10 pt-6 border-t border-accent">
            <p className="label-gap mb-3">Observation</p>
            <p className="text-lg sm:text-xl text-foreground font-editorial leading-snug max-w-3xl">
              {t('landing.compassInsight')}
            </p>
          </div>
        </div>
      </section>

      <SectionRule />

      {/* ── V. The questions ── */}
      {Array.isArray(FAQ_ITEMS) && FAQ_ITEMS.length > 0 && (
        <section className="px-6" data-testid="faq-section">
          <div className="max-w-3xl mx-auto">
            <SectionMarker numeral="V." label={t('landing.faqTitle') || 'Questions'} />
            <div className="border-t border-border">
              {FAQ_ITEMS.map((item, i) => (
                <div key={`faq-${i}`} className="border-b border-border" data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-baseline justify-between py-5 text-left text-foreground transition-colors"
                  >
                    <span className="text-sm pr-6">{item.q}</span>
                    <span className="font-mono text-xs text-muted-foreground shrink-0">
                      {openFaq === i ? '[−]' : '[+]'}
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="pb-6 text-sm text-muted-foreground leading-relaxed font-editorial animate-fade-in">
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <SectionRule />

      {/* ── VI. Threshold ── */}
      <section className="px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <SectionMarker numeral="VI." label="Threshold" />
          <h2 className="text-2xl sm:text-3xl tracking-tight text-foreground">
            {t('landing.readyToDiscover')}
          </h2>
          <p className="mt-4 text-sm text-muted-foreground font-editorial max-w-2xl">
            {t('landing.everyLifeWorthMapping')}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              className="button-mirror"
              onClick={() => { setShowAuth(true); setIsLogin(false); }}
              data-testid="cta-get-started-btn"
            >
              {t('landing.beginJourney')}
            </Button>
            <Link to="/pricing" className="button-mirror button-mirror--ghost no-underline" data-testid="pricing-link">
              {t('landing.viewPricing')}
            </Link>
          </div>
          <p className="mt-12 text-xs text-muted-foreground font-editorial italic" data-testid="hero-signoff">
            {t('landing.footerSignoff')}
          </p>
        </div>
      </section>

      <Footer />

      {/* ── Auth Modal ── */}
      {showAuth && (
        <div
          className="fixed inset-0 modal-overlay z-50 flex items-center justify-center p-4"
          onClick={() => setShowAuth(false)}
        >
          <div
            className="modal-content p-8 w-full max-w-md animate-fade-in"
            onClick={e => e.stopPropagation()}
          >
            <p className="mirror-meta">
              {isLogin ? t('auth.welcomeBack') : t('auth.createAccount')}
            </p>
            <h2 className="mt-2 text-xl tracking-tight text-foreground">
              {isLogin ? t('auth.signIn') : t('auth.signUp')}
            </h2>
            <hr className="rule my-6" />
            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">{t('auth.name')}</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-journal mt-2"
                    placeholder={t('auth.namePlaceholder')}
                    required={!isLogin}
                    data-testid="auth-name-input"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-journal mt-2"
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  data-testid="auth-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-journal mt-2"
                  placeholder="********"
                  required
                  data-testid="auth-password-input"
                />
              </div>
              {error && (
                <p className="text-xs text-accent font-mono uppercase tracking-wider" data-testid="auth-error">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                className="button-mirror w-full"
                disabled={isLoading}
                data-testid="auth-submit-btn"
              >
                {isLoading ? t('auth.pleaseWait') : (isLogin ? t('auth.signIn') : t('auth.signUp'))}
              </Button>
            </form>
            {isLogin && (
              <p className="text-center mt-4">
                <Link
                  to="/forgot-password"
                  className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword') || 'Forgot your password?'}
                </Link>
              </p>
            )}
            <p className="text-center text-muted-foreground mt-6 text-xs">
              {isLogin ? t('auth.noAccount') + ' ' : t('auth.haveAccount') + ' '}
              <button
                className="text-foreground underline underline-offset-4 hover:no-underline transition-all"
                onClick={() => { setIsLogin(!isLogin); setError(''); }}
                data-testid="auth-toggle-btn"
              >
                {isLogin ? t('auth.signUp') : t('auth.signIn')}
              </button>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
